class WFaqComponent extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.onTabClick = this.onTabClick.bind(this);
    this.onAccordionToggle = this.onAccordionToggle.bind(this);
    this.onAccordionClick = this.onAccordionClick.bind(this);

    this.initTabs();
    this.initAccordions();
  }

  disconnectedCallback() {
    if (this.tabButtons && this.tabButtons.length) {
      this.tabButtons.forEach((button) => {
        button.removeEventListener('click', this.onTabClick);
      });
    }

    if (this.accordions && this.accordions.length) {
      this.accordions.forEach((accordion) => {
        accordion.removeEventListener('toggle', this.onAccordionToggle);
        const summary = accordion.querySelector('.w-faq__accordion-summary');
        if (summary) {
          summary.removeEventListener('click', this.onAccordionClick);
        }
      });
    }

    this.initialized = false;
  }

  initTabs() {
    this.tabsContainer = this.querySelector('[data-faq-tabs]');
    this.tabButtons = Array.from(this.querySelectorAll('[data-faq-tab-trigger]'));
    this.tabPanels = Array.from(this.querySelectorAll('[data-faq-tab-panel]'));
    this.activeTab = '';

    if (!this.tabsContainer || !this.tabButtons.length || !this.tabPanels.length) return;

    this.tabsContainer.style.setProperty('--faq-tab-count', `${this.tabButtons.length}`);

    let defaultTab = this.tabButtons[0].dataset.faqTabTrigger || '';

    this.tabButtons.forEach((button, index) => {
      button.dataset.faqTabIndex = `${index}`;
      if (button.classList.contains('is-active') && button.dataset.faqTabTrigger) {
        defaultTab = button.dataset.faqTabTrigger;
      }
      button.addEventListener('click', this.onTabClick);
    });

    if (defaultTab) {
      this.setActiveTab(defaultTab);
    }
  }

  onTabClick(event) {
    const button = event.currentTarget;
    if (!(button instanceof HTMLElement)) return;
    const tabId = button.dataset.faqTabTrigger || '';
    if (!tabId || tabId === this.activeTab) return;
    this.setActiveTab(tabId);
  }

  setActiveTab(tabId) {
    this.activeTab = tabId;

    this.tabButtons.forEach((button) => {
      const isActive = button.dataset.faqTabTrigger === tabId;
      button.classList.toggle('is-active', isActive);
      button.setAttribute('aria-selected', isActive ? 'true' : 'false');
      button.setAttribute('tabindex', isActive ? '0' : '-1');
      if (isActive && this.tabsContainer) {
        this.tabsContainer.style.setProperty('--faq-tab-index', button.dataset.faqTabIndex || '0');
      }
    });

    this.tabPanels.forEach((panel) => {
      const isActive = panel.dataset.faqTabPanel === tabId;
      panel.hidden = !isActive;
      if (!isActive) return;
      this.syncPanelAccordionHeights(panel);
    });
  }

  initAccordions() {
    this.accordions = Array.from(this.querySelectorAll('.w-faq__accordion'));
    if (!this.accordions.length) return;

    this.accordions.forEach((accordion) => {
      const content = accordion.querySelector('.w-faq__accordion-content');
      const summary = accordion.querySelector('.w-faq__accordion-summary');
      if (!content) return;
      accordion.addEventListener('toggle', this.onAccordionToggle);
      if (summary) {
        summary.addEventListener('click', this.onAccordionClick);
      }
      if (accordion.hasAttribute('open')) {
        content.style.height = 'auto';
      } else {
        content.style.height = '0px';
      }
    });
  }

  onAccordionToggle(event) {
    const accordion = event.target;
    if (!(accordion instanceof HTMLElement)) return;
    const content = accordion.querySelector('.w-faq__accordion-content');
    if (!content) return;

    if (accordion.open) {
      content.style.height = `${content.scrollHeight}px`;
    } else {
      content.style.height = `${content.scrollHeight}px`;
      requestAnimationFrame(() => {
        content.style.height = '0px';
      });
    }
  }

  onAccordionClick(event) {
    event.preventDefault();
    const summary = event.currentTarget;
    if (!(summary instanceof HTMLElement)) return;

    const accordion = summary.closest('.w-faq__accordion');
    if (!accordion) return;

    const content = accordion.querySelector('.w-faq__accordion-content');
    if (!content) return;

    const parentList = accordion.closest('.w-faq__accordion-list');
    const siblings = parentList
      ? Array.from(parentList.querySelectorAll('.w-faq__accordion'))
      : this.accordions;

    if (accordion.open) {
      content.style.height = `${content.scrollHeight}px`;
      requestAnimationFrame(() => {
        content.style.height = '0px';
      });
      content.addEventListener(
        'transitionend',
        () => {
          accordion.open = false;
        },
        { once: true }
      );
      return;
    }

    siblings.forEach((item) => {
      if (item === accordion || !item.open) return;
      const itemContent = item.querySelector('.w-faq__accordion-content');
      if (!itemContent) return;
      itemContent.style.height = `${itemContent.scrollHeight}px`;
      requestAnimationFrame(() => {
        itemContent.style.height = '0px';
      });
      itemContent.addEventListener(
        'transitionend',
        () => {
          item.open = false;
        },
        { once: true }
      );
    });

    accordion.open = true;
    const targetHeight = content.scrollHeight;
    content.style.height = '0px';
    requestAnimationFrame(() => {
      content.style.height = `${targetHeight}px`;
    });
    content.addEventListener(
      'transitionend',
      () => {
        content.style.height = 'auto';
      },
      { once: true }
    );
  }

  syncPanelAccordionHeights(panel) {
    const panelAccordions = panel.querySelectorAll('.w-faq__accordion');
    panelAccordions.forEach((accordion) => {
      const content = accordion.querySelector('.w-faq__accordion-content');
      if (!content) return;
      content.style.height = accordion.open ? 'auto' : '0px';
    });
  }
}

if (!customElements.get('w-faq-component')) {
  customElements.define('w-faq-component', WFaqComponent);
}
